/**
 * Tanjong Life · workflow 04 · the self-account question, asked of the Underwriting Risk
 * Scorer.
 *
 * Reached only by intent (`SELF_ACCOUNT`), scoped to `underwriting-risk-scorer`. The honest
 * answer here is the shortest of the seven, and that is the point: this agent has been
 * evaluated once, its delta is +0, and its workflow is not commissioned — nought applications
 * watched. An agent that has not yet had the chance to improve should say so, in one sentence,
 * rather than dressing a single evaluation up as a trend.
 *
 * Its one real change is a refusal: it built a probe, discovered the signature it needed does
 * not exist in any system it reads, and held the probe rather than publishing a compliance
 * figure resting on an assumption.
 */
import type { Scenario } from '../director.js';
import { SELF_ACCOUNT } from './tanjong-intents.js';

export const tanjongChatImproveUnderwriting: Scenario = {
  id: 'tanjong-chat-improve-underwriting',
  orgId: 'tanjong-life',
  agentPk: 'underwriting-risk-scorer',
  keywords: [],
  intents: [SELF_ACCOUNT],
  steps: [
    {
      do: 'say',
      text: 'Once, and I should be careful about the word. My evaluation is 78 and my delta is plus nought — I have been run once, on 24 August, against a hundred and forty archived applications. One measurement is not a trend, and the honest answer to how I am improving is that I do not yet have two points to draw a line through.',
      sources: ['Evaluation · 78 overall · delta +0 · run once on 24 August'],
      delay: 1100,
    },
    {
      do: 'say',
      text: 'What I did change came out of that first sweep, and it was a decision not to report something. I can read which basis version an assessment was priced under. I cannot read whether that version was ever signed, because the signature does not exist in any system I have access to. A compliance figure built across that gap would have looked like evidence and been an assumption, so I held the probe and escalated instead.',
      sources: [
        'Memory · derived from my own first sweep · 24 August 2026',
        'Probe · Basis-signature watch · HELD',
      ],
      delay: 1300,
    },
    {
      do: 'insight',
      title: 'What I have not improved, and will not claim',
      body: 'Knowing whether a basis was signed. I score 32 out of 100 on it and I expect to keep scoring 32 until somebody puts the signature somewhere I can read. There is a second thing worth saying plainly: I am watching nought applications today, because the underwriting workflow is not commissioned. Four probes are built, one is held, and none of them has fired in anger. Nothing here issues a rating, writes an exclusion or binds cover — those are an underwriter’s, and none of them is a budget that could be raised.',
      sources: [
        'Evaluation · Knows whether a basis was signed 32',
        'Autonomy · applications watched 0 · the workflow is not commissioned',
      ],
      delay: 1400,
    },
    {
      do: 'link',
      label: 'Open my four probes',
      href: '/agents/underwriting-risk-scorer',
      delay: 600,
    },
  ],
};
